import React from 'react';

// Component now accepts totalItemsValue for the new percentage calculation
const DutyOrbitCard = ({ formData, estimateSummary, totalItemsValue }) => {
    // Define the structure for all potential tax types and their colors
    const defaultTaxConfig = {
        duty: { label: "Duty", primary: "#ff6a24", secondary: "#ffb347" },
        vat: { label: "VAT", primary: "#1c58f4", secondary: "#6ec1ff" },
        taxes: { label: "Taxes", primary: "#00b76b", secondary: "#4de38b" }
    };

    // New Reference Value: 110% of Total Items Value (CIF reference)
    // CRITICAL CHANGE: This replaces 'total' in percentage calculation
    const cifReferenceValue = (totalItemsValue || 0) * 1.10;

    // Extract tax data from estimateSummary
    const getTaxData = () => {
        if (!estimateSummary || !estimateSummary.dhlTaxes || estimateSummary.dhlTaxes.length === 0) {
            // Return zero for all taxes if no estimate summary or taxes exist
            return { duty: 0, vat: 0, taxes: 0, hasData: false };
        }

        const taxes = estimateSummary.dhlTaxes;
        const taxMap = {
            duty: 0,
            vat: 0,
            taxes: 0,
            hasData: true
        };

        taxes.forEach(tax => {
            const taxName = String(tax.name || "").trim().toUpperCase();
            const taxValue = parseFloat(tax.value) || 0;
            if (taxName === 'DUTY') {
                taxMap.duty += taxValue;
            } else if (taxName.includes('VAT') || taxName === 'GST' || taxName === 'SALE') {
                // IMPORTANT: VAT, GST, and SALE are summed into the 'vat' category
                taxMap.vat += taxValue;
            } else if (taxName === 'TAXES') {
                taxMap.taxes += taxValue;
            }
        });

        return taxMap;
    };

    const taxData = getTaxData();

    // Build dynamic data array and items array
    const buildChartData = () => {
        const chartData = [];
        const itemsData = [];

        // Determine which taxes are present
        const hasVat = taxData.vat > 0;
        const hasDuty = taxData.duty > 0;
        const hasTaxes = taxData.taxes > 0;
        const hasAnyData = hasVat || hasDuty || hasTaxes;

        // Use the reference value only if it's a positive number to prevent division by zero
        const effectiveReference = cifReferenceValue > 0 ? cifReferenceValue : 1;

        // Helper to calculate percentage
        const getPercentage = (value) => {
            const percentage = hasAnyData ? (value / effectiveReference) * 100 : 0;
            return Math.min(100, Math.max(0, percentage));
        };

        // Helper to create data entry
        const createEntry = (key, value, isEmpty = false) => {
            const config = defaultTaxConfig[key];
            return {
                chart: {
                    name: config.label,
                    value: isEmpty ? 0 : getPercentage(value),
                    fill: `url(#${key}Gradient)`
                },
                item: {
                    label: config.label,
                    color: config.primary,
                    value: isEmpty ? undefined : value
                }
            };
        };

        // SCENARIO HANDLING - Always show exactly 2 pie lines
        if (!hasAnyData) {
            // Initial state: Show VAT and Duty as empty
            const vatEntry = createEntry('vat', 0, true);
            const dutyEntry = createEntry('duty', 0, true);
            chartData.push(vatEntry.chart, dutyEntry.chart);
            itemsData.push(vatEntry.item, dutyEntry.item);
        }
        else if (hasVat && hasDuty && !hasTaxes) {
            // VAT + Duty only: Show both
            const vatEntry = createEntry('vat', taxData.vat);
            const dutyEntry = createEntry('duty', taxData.duty);
            chartData.push(vatEntry.chart, dutyEntry.chart);
            itemsData.push(vatEntry.item, dutyEntry.item);
        }
        else if (hasVat && !hasDuty && !hasTaxes) {
            // Only VAT: VAT filled, Duty empty
            const vatEntry = createEntry('vat', taxData.vat);
            const dutyEntry = createEntry('duty', 0, true);
            chartData.push(vatEntry.chart, dutyEntry.chart);
            itemsData.push(vatEntry.item, dutyEntry.item);
        }
        else if (!hasVat && hasDuty && !hasTaxes) {
            // Only Duty: VAT empty, Duty filled
            const vatEntry = createEntry('vat', 0, true);
            const dutyEntry = createEntry('duty', taxData.duty);
            chartData.push(vatEntry.chart, dutyEntry.chart);
            itemsData.push(vatEntry.item, dutyEntry.item);
        }
        else if (!hasVat && !hasDuty && hasTaxes) {
            // Only Taxes: VAT empty, Taxes filled
            const vatEntry = createEntry('vat', 0, true);
            const taxesEntry = createEntry('taxes', taxData.taxes);
            chartData.push(vatEntry.chart, taxesEntry.chart);
            itemsData.push(vatEntry.item, taxesEntry.item);
        }
        else if (hasVat && !hasDuty && hasTaxes) {
            // VAT + Taxes: VAT filled, Taxes filled
            const vatEntry = createEntry('vat', taxData.vat);
            const taxesEntry = createEntry('taxes', taxData.taxes);
            chartData.push(vatEntry.chart, taxesEntry.chart);
            itemsData.push(vatEntry.item, taxesEntry.item);
        }
        else if (!hasVat && hasDuty && hasTaxes) {
            // Duty + Taxes: Duty filled, Taxes filled
            const dutyEntry = createEntry('duty', taxData.duty);
            const taxesEntry = createEntry('taxes', taxData.taxes);
            chartData.push(dutyEntry.chart, taxesEntry.chart);
            itemsData.push(dutyEntry.item, taxesEntry.item);
        }
        else if (hasVat && hasDuty && hasTaxes) {
            // All three present: Show VAT, Duty, and Taxes (3 lines)
            const vatEntry = createEntry('vat', taxData.vat);
            const dutyEntry = createEntry('duty', taxData.duty);
            const taxesEntry = createEntry('taxes', taxData.taxes);
            chartData.push(vatEntry.chart, dutyEntry.chart, taxesEntry.chart);
            itemsData.push(vatEntry.item, dutyEntry.item, taxesEntry.item);
        }

        return { chartData, itemsData, colors: defaultTaxConfig };
    };

    const { chartData, itemsData, colors } = buildChartData();

    const radiusForIndex = (index) => 88 - index * 18;
    const ringStroke = 12;
    const buildRingStyle = (item, index) => {
        const radius = radiusForIndex(index);
        const circumference = 2 * Math.PI * radius;
        const progress = Math.min(100, Math.max(0, Number(item.value) || 0));

        return {
            radius,
            circumference,
            strokeDasharray: `${(progress / 100) * circumference} ${circumference}`,
        };
    };

    // Determine font size based on service name length
    const getServiceFontSize = () => {
        const serviceName = formData.selectedService || "";
        if (serviceName.length > 8 || serviceName === "EOR & IOR") {
            return "text-base"; // Smaller for long names like "EOR & IOR"
        }
        return "text-xl"; // Default size
    };

    // The component now always renders the main JSX structure.
    return (
        <div className="flex flex-col items-start justify-center gap-4">
            <div className="flex items-center flex-col md:flex-row justify-center gap-4 w-full">
                <div className="relative">
                    <div className={`absolute top-1/2 left-1/2 whitespace-nowrap -translate-x-1/2 -translate-y-1/2 p-6 size-24 flex items-center justify-center rounded-full shadow-xl bg-gray-800 ${getServiceFontSize()} text-white`}>
                        {formData.selectedService}
                    </div>

                    <svg width="250" height="250" viewBox="0 0 250 250" className="block">
                        <defs>
                            {/* Duty Gradient */}
                            <linearGradient id="dutyGradient" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor={colors.duty.primary} />
                                <stop offset="100%" stopColor={colors.duty.secondary} />
                            </linearGradient>

                            {/* VAT Gradient */}
                            <linearGradient id="vatGradient" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor={colors.vat.primary} />
                                <stop offset="100%" stopColor={colors.vat.secondary} />
                            </linearGradient>

                            {/* Taxes Gradient */}
                            <linearGradient id="taxesGradient" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor={colors.taxes.primary} />
                                <stop offset="100%" stopColor={colors.taxes.secondary} />
                            </linearGradient>
                        </defs>
                        {chartData.map((item, index) => {
                            const ring = buildRingStyle(item, index);

                            return (
                                <g key={item.name}>
                                    <circle
                                        cx="125"
                                        cy="125"
                                        r={ring.radius}
                                        fill="none"
                                        stroke="#e5e7eb"
                                        strokeWidth={ringStroke}
                                    />
                                    <circle
                                        cx="125"
                                        cy="125"
                                        r={ring.radius}
                                        fill="none"
                                        stroke={item.fill}
                                        strokeWidth={ringStroke}
                                        strokeLinecap="round"
                                        strokeDasharray={ring.strokeDasharray}
                                        transform="rotate(-90 125 125)"
                                    />
                                </g>
                            );
                        })}
                    </svg>
                </div>

                {/* Legend */}
                <div className="flex flex-col gap-2">
                    {itemsData.map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <span
                                className="inline-block w-3 h-3 rounded"
                                style={{ backgroundColor: item.color }}
                            />
                            {/* Value display removed as per previous request */}
                            <span className="text-sm">
                                {item.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Tags / chips */}
            <div className="flex items-center flex-wrap justify-start gap-2">
                {itemsData.map((item, i) => (
                    <div
                        key={i}
                        className="flex items-center gap-2 rounded-full px-3 py-1 border border-gray-600"
                    >
                        {/* Font-semibold removed as per previous request */}
                        <span className="text-sm">{item.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DutyOrbitCard;
